import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  ArrowLeft, Plus, X, Pencil, Trash2, MapPin, Warehouse,
} from 'lucide-react';

interface ServiceLocation {
  id: string;
  name: string;
  city: string;
  createdAt?: string;
}

const inputClass = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50 transition-colors';
const labelClass = 'block text-xs text-fieldiq-text mb-1.5 font-medium';

export default function ServiceLocationsScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<ServiceLocation | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['service-locations'],
    queryFn: () => api.serviceLocations.list().then(r => r.data.data ?? r.data ?? []),
  });
  const locations: ServiceLocation[] = data ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['service-locations'] });

  const createMutation = useMutation({
    mutationFn: (d: any) => api.serviceLocations.create(d),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: d }: { id: string; data: any }) => api.serviceLocations.update(id, d),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.serviceLocations.delete(id),
    onSuccess: invalidate,
  });

  const handleSave = (item: { id?: string; name: string; city: string }) => {
    if (editItem) {
      updateMutation.mutate({ id: editItem.id, data: { name: item.name, city: item.city } });
    } else {
      createMutation.mutate({ name: item.name, city: item.city });
    }
    setShowModal(false);
    setEditItem(null);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
    setDeleteConfirm(null);
  };

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        
      </div>

      {/* Title bar */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="text-base font-bold text-foreground">Service Locations</h1>
            <p className="text-sm text-fieldiq-text">{locations.length} shops</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setEditItem(null); setShowModal(true); }}
              className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors"
            >
              <Plus size={16} /> New Shop
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12 text-fieldiq-text">Loading shops...</div>
      )}

      {/* List */}
      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-3">
        {!isLoading && locations.length === 0 && (
          <div className="text-center py-12">
            <Warehouse size={40} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-fieldiq-text">No service locations yet.</p>
            <p className="text-xs text-fieldiq-muted mt-1">Add your first shop to start tracking equipment locations.</p>
          </div>
        )}

        {locations.map(loc => (
          <div key={loc.id} className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-fieldiq-accent/15 flex items-center justify-center flex-shrink-0">
                  <Warehouse size={18} className="text-fieldiq-accent" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{loc.name}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-fieldiq-text">
                    <MapPin size={12} />
                    <span>{loc.city}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setEditItem(loc); setShowModal(true); }}
                  className="p-1.5 rounded-lg hover:bg-fieldiq-dark text-fieldiq-muted hover:text-white transition-colors"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => setDeleteConfirm(loc.id)}
                  className="p-1.5 rounded-lg hover:bg-fieldiq-dark text-fieldiq-muted hover:text-red-400 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-2">Delete Shop?</h3>
            <p className="text-sm text-fieldiq-text mb-6">Equipment assigned to this shop will keep their current assignment until transferred.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-lg text-sm font-medium">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-500 text-white py-2.5 rounded-lg text-sm font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <AddEditModal
          item={editItem}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditItem(null); }}
        />
      )}
    </div>
  );
}

function AddEditModal({ item, onSave, onClose }: {
  item: ServiceLocation | null;
  onSave: (data: { id?: string; name: string; city: string }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [city, setCity] = useState(item?.city ?? '');

  const handleSubmit = () => {
    if (!name.trim() || !city.trim()) return;
    onSave({ id: item?.id, name: name.trim(), city: city.trim() });
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-fieldiq-card border border-fieldiq-border rounded-xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-fieldiq-border">
          <h2 className="text-lg font-bold text-white">{item ? 'Edit Shop' : 'Add Shop'}</h2>
          <button onClick={onClose} className="text-fieldiq-muted hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className={labelClass}>Shop Name *</label>
            <input
              className={inputClass}
              placeholder="e.g. Grande Prairie Shop"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className={labelClass}>City *</label>
            <input
              className={inputClass}
              placeholder="e.g. Grande Prairie"
              value={city}
              onChange={e => setCity(e.target.value)}
            />
          </div>
        </div>

        <div className="p-5 border-t border-fieldiq-border">
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || !city.trim()}
            className="w-full bg-fieldiq-accent text-fieldiq-dark py-3 rounded-xl font-semibold text-sm hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {item ? 'Save Changes' : 'Add Shop'}
          </button>
        </div>
      </div>
    </div>
  );
}
